import moment from 'moment';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme
} from '@mui/material';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import GroupsTwoToneIcon from '@mui/icons-material/GroupsTwoTone';
import PhoneInTalkTwoToneIcon from '@mui/icons-material/PhoneInTalkTwoTone';

const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

const relative = (value) => value && moment(value).isValid() ? moment(value).fromNow(true) : '';
const statusLabel = (employee) => {
  if (employee.isTower) return 'Office account';
  if (employee.presence === 'unknown' || employee.callState === 'unknown') return 'Availability unknown';
  if (employee.callState === 'on_call') return 'On a call';
  if (employee.callState === 'ringing') return 'Ringing';
  if (employee.presence === 'online') return employee.acceptingCalls ? 'Online · accepting calls' : 'Online';
  if (employee.presence === 'away') return 'Away';
  return employee.lastSeenAt ? `Offline · ${relative(employee.lastSeenAt)} ago` : 'Offline';
};

function TeamDirectory({
  employees,
  groups,
  viewer,
  loading,
  error,
  search,
  activeConversationId,
  onSelectEmployee,
  onSelectGroup,
  onCreateGroup,
  onRetry
}) {
  const theme = useTheme();
  const needle = String(search || '').trim().toLowerCase();
  const visibleEmployees = employees.filter((employee) => !needle || [
    employee.name, employee.role, employee.email
  ].some((value) => String(value || '').toLowerCase().includes(needle)));
  const visibleGroups = groups.filter((group) => !needle || String(group.name || '').toLowerCase().includes(needle));

  if (loading && !employees.length && !groups.length) {
    return <Box py={6} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
  }
  if (error && !employees.length && !groups.length) {
    return (
      <Box px={2} py={5} textAlign="center">
        <Typography color="error" variant="body2">{error}</Typography>
        <Button size="small" sx={{ mt: 1 }} onClick={onRetry}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box>
      {error ? (
        <Box px={2} py={1} role="status">
          <Typography variant="body2">{error}</Typography>
          <Button size="small" onClick={onRetry}>Retry</Button>
        </Box>
      ) : null}
      <Box px={1.25} pt={0.5} pb={0.25} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="overline" color="text.secondary">Groups</Typography>
        {viewer?.canManageGroups ? (
          <Button size="small" startIcon={<AddTwoToneIcon />} onClick={onCreateGroup}>New group</Button>
        ) : null}
      </Box>
      <List disablePadding>
        {visibleGroups.map((group) => (
          <ListItemButton
            key={group.id}
            selected={activeConversationId === group.id}
            onClick={() => onSelectGroup(group)}
            sx={{ borderRadius: 1, mb: 0.25, py: 0.85 }}
          >
            <ListItemAvatar sx={{ minWidth: 44 }}>
              <Badge color="error" badgeContent={group.unreadCount || 0} invisible={!group.unreadCount} max={99}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: theme.colors.primary.lighter, color: 'primary.main' }}>
                  <GroupsTwoToneIcon fontSize="small" />
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={<Typography variant="body2" noWrap fontWeight={group.unreadCount ? 700 : 600}>{group.name}</Typography>}
              secondary={(
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {group.lastMessage || `${group.memberCount || 0} members`}
                </Typography>
              )}
            />
            <Typography variant="caption" color="text.secondary">{relative(group.lastMessageAt)}</Typography>
          </ListItemButton>
        ))}
        {!visibleGroups.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, py: 1 }}>
            {needle ? 'No matching groups.' : 'No team groups yet.'}
          </Typography>
        ) : null}
      </List>

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 1.25, pt: 1.5, pb: 0.25 }}>
        People
      </Typography>
      <List disablePadding>
        {visibleEmployees.map((employee) => {
          const selected = employee.conversationId && employee.conversationId === activeConversationId;
          const self = Boolean(employee.isSelf);
          const dot = employee.callState === 'on_call' ? '#2563EB'
            : employee.callState === 'ringing' ? '#E07A12'
              : employee.presence === 'online' ? '#16A36A'
                : employee.presence === 'away' ? '#D89A16' : '#A8B0BC';
          return (
            <ListItemButton
              key={employee.id}
              selected={selected}
              disabled={self}
              onClick={() => onSelectEmployee(employee)}
              sx={{ borderRadius: 1, mb: 0.25, py: 0.85 }}
            >
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Badge color="error" badgeContent={employee.unreadCount || 0} invisible={!employee.unreadCount} max={99}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar sx={{ width: 36, height: 36, fontSize: 12 }}>{initials(employee.name)}</Avatar>
                    <Box sx={{
                      position: 'absolute', width: 10, height: 10, borderRadius: '50%', bgcolor: dot,
                      border: '2px solid', borderColor: 'background.paper', right: -1, bottom: -1
                    }} />
                  </Box>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                    <Typography variant="body2" noWrap fontWeight={employee.unreadCount ? 700 : 600}>{employee.name}</Typography>
                    {self ? <Chip size="small" label="You" sx={{ height: 18, fontSize: 10 }} /> : null}
                  </Box>
                )}
                secondary={<Typography variant="caption" color="text.secondary" noWrap display="block">{statusLabel(employee)}</Typography>}
              />
              {['on_call', 'ringing'].includes(employee.callState) ? <PhoneInTalkTwoToneIcon color="primary" fontSize="small" /> : null}
            </ListItemButton>
          );
        })}
      </List>
      {!visibleEmployees.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, py: 1 }}>
          No matching employees.
        </Typography>
      ) : null}
    </Box>
  );
}

export default TeamDirectory;
